import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Spinner from './Spinner';
import formatNumber from '../utils/format';
import { useUser } from "@clerk/clerk-react";
import PricingModal from "./modals/PricingModal";
import AudienceCard from './AudienceCard';
import AddAudienceCard from './AddAudienceCard';
import checkSubscriptionStatus from '../utils/subscription';
import { fetchDashboardAudienceData } from '../data/audiences';
import { Plus, X, ChevronDown } from 'lucide-react';

const Dashboard = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const isNewSignUp = searchParams.get('signedUp') === 'true';
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [audienceMetrics, setAudienceMetrics] = useState({});
  const [curatedAudiences, setCuratedAudiences] = useState({});
  const [customAudiences, setCustomAudiences] = useState({});

  // Custom audience modal state
  const [showCustomAudienceModal, setShowCustomAudienceModal] = useState(false);
  const [customAudienceName, setCustomAudienceName] = useState('');
  const [isCreatingAudience, setIsCreatingAudience] = useState(false);
  const [customAudienceError, setCustomAudienceError] = useState('');

  // Filter state
  const [filterType, setFilterType] = useState('default'); // 'default', 'alphabetical-asc', 'alphabetical-desc', 'size-asc', 'size-desc'
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('curated');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortDropdown && !event.target.closest('.sort-dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);

  // Function to sort audiences based on filter type
  const getSortedAudiences = (audienceEntries) => {
    if (!audienceEntries || !Array.isArray(audienceEntries)) {
      return [];
    }
    
    switch (filterType) {
      case 'alphabetical-asc':
        return audienceEntries.sort(([a], [b]) => (a || '').localeCompare(b || ''));
      case 'alphabetical-desc':
        return audienceEntries.sort(([a], [b]) => (b || '').localeCompare(a || ''));
      case 'size-asc':
        return audienceEntries.sort(([a], [b]) => {
          const aMetrics = audienceMetrics[a] || { totalAudience: 0 };
          const bMetrics = audienceMetrics[b] || { totalAudience: 0 };
          return (aMetrics.totalAudience || 0) - (bMetrics.totalAudience || 0);
        });
      case 'size-desc':
        return audienceEntries.sort(([a], [b]) => {
          const aMetrics = audienceMetrics[a] || { totalAudience: 0 };
          const bMetrics = audienceMetrics[b] || { totalAudience: 0 };
          return (bMetrics.totalAudience || 0) - (aMetrics.totalAudience || 0);
        });
      default:
        return audienceEntries; // Default order
    }
  };

  // Fetch all audience data from the database
  const fetchAudienceData = async () => {
    setLoading(true);
    setError('');
    try {
      const {
        curated,
        custom,
        metrics,
      } = await fetchDashboardAudienceData(baseUrl);
      setCuratedAudiences(curated);
      setCustomAudiences(custom);
      setAudienceMetrics(metrics);
    } catch (error) {
      console.error('Error fetching audience data:', error);
      setError(error.message);
      // Fallback to empty objects if fetch fails
      setCuratedAudiences({});
      setCustomAudiences({});
      setAudienceMetrics({});
    } finally {
      setLoading(false);
    }
  };

  // Create custom audience
  const handleCreateCustomAudience = async () => {
    if (!customAudienceName.trim()) {
      setCustomAudienceError('Please enter an audience name');
      return;
    }

    // Check if audience already exists in either curated or custom
    if ((curatedAudiences && curatedAudiences[customAudienceName.trim()]) || 
        (customAudiences && customAudiences[customAudienceName.trim()])) {
      setCustomAudienceError('This audience already exists');
      return;
    }

    setIsCreatingAudience(true);
    setCustomAudienceError('');

    try {
      // Call the create-audience endpoint
      const response = await fetch(`${baseUrl}/v1/create-audience`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audienceName: customAudienceName.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to create custom audience');
      }

      const result = await response.json();
      console.log('Custom audience created:', result);

      // Refresh the audience data to include the new custom audience
      await fetchAudienceData();

      // Reset form and close modal
      setCustomAudienceName('');
      setShowCustomAudienceModal(false);
      setIsCreatingAudience(false);

    } catch (error) {
      console.error('Error creating custom audience:', error);
      setCustomAudienceError(error.message || 'Failed to create audience. Please try again.');
      setIsCreatingAudience(false);
    }
  };

  // Close custom audience modal
  const handleCloseCustomAudienceModal = () => {
    setShowCustomAudienceModal(false);
    setCustomAudienceName('');
    setCustomAudienceError('');
  };

  useEffect(() => {
    async function checkProStatus() {
      setIsLoadingStatus(true);
      try {
        const subscriptionData = await checkSubscriptionStatus();

        if (subscriptionData && typeof subscriptionData.isPro === 'boolean') {
          setIsPro(subscriptionData.isPro);
        } else {
          console.warn('Invalid subscription data received:', subscriptionData);
          setIsPro(false);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setIsPro(false);
      } finally {
        setIsLoadingStatus(false);
      }
    }

    checkProStatus();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setIsPro(true);
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (user && isNewSignUp) {
      localStorage.setItem(`welcomeModalShown_${user.id}`, 'true');
      setShowPricingModal(true);
    }
  }, [isNewSignUp, user]);

  // Fetch audience data on component mount
  useEffect(() => {
    fetchAudienceData();
  }, []);

  const handleSelectPlan = async (priceId) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const requestUrl = `${baseUrl}/api/v1/create-checkout-session`;

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          userId: user.id
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No URL in response');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    }
  };

  return (
    <div className="pt-20 px-4 min-h-screen bg-[#1A2238]">
      <div className="min-h-screen bg-[#121212]">
        <div className="border-b border-gray-800 p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-white">Dashboard</h1>
              {!isLoadingStatus && !isPro && (
                <button
                  onClick={() => setShowPricingModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-2 px-4 rounded transition-colors"
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          {loading && <Spinner />}

          <div className="mb-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-white">Audiences</h2>
            </div>

            {/* Sort Dropdown */}
            <div className="mb-6 relative sort-dropdown">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded transition-colors"
              >
                <span>Sort</span>
                <ChevronDown size={16} className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg z-10 min-w-[150px]">
                  <button
                    onClick={() => {
                      setFilterType('alphabetical-asc');
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                      filterType === 'alphabetical-asc' ? 'text-indigo-400' : 'text-gray-300'
                    }`}
                  >
                    A-Z
                  </button>
                  <button
                    onClick={() => {
                      setFilterType('alphabetical-desc');
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                      filterType === 'alphabetical-desc' ? 'text-indigo-400' : 'text-gray-300'
                    }`}
                  >
                    Z-A
                  </button>
                  <button
                    onClick={() => {
                      setFilterType('size-desc');
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                      filterType === 'size-desc' ? 'text-indigo-400' : 'text-gray-300'
                    }`}
                  >
                    Largest First
                  </button>
                  <button
                    onClick={() => {
                      setFilterType('size-asc');
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                      filterType === 'size-asc' ? 'text-indigo-400' : 'text-gray-300'
                    }`}
                  >
                    Smallest First
                  </button>
                </div>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setActiveTab('curated')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'curated'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Curated
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'custom'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Custom
              </button>
            </div>

            {/* Audience Grid */}
            <div className="mb-8">
              {activeTab === 'curated' ? (
                curatedAudiences && Object.keys(curatedAudiences).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getSortedAudiences(Object.entries(curatedAudiences)).map(([audience, subreddits]) => {
                      const metrics = audienceMetrics[audience] || { 
                        totalAudience: 0, 
                        growthPercentage: 0, 
                        isIncrease: true 
                      };
                      
                      return (
                        <Link 
                          to={`/audience/${audience}`} 
                          state={{ subreddits }} 
                          key={audience}
                          className="hover:opacity-90 transition-opacity"
                        >
                          <AudienceCard 
                            title={audience}
                            value={formatNumber(metrics.totalAudience)}
                            growth={`${metrics.isIncrease ? '+' : '-'}${Math.abs(metrics.growthPercentage)}%`}
                            period="vs last month"
                            subreddits={subreddits}
                            isGrowthPositive={metrics.isIncrease}
                            isLoading={loading && (!subreddits || subreddits.length === 0)}
                            isPro={isPro}
                          />
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    {loading ? "Loading curated audiences..." : "No curated audiences available"}
                  </div>
                )
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Add Custom Audience Card - Always first */}
                  <AddAudienceCard onClick={() => setShowCustomAudienceModal(true)} />
                  
                  {/* Existing Custom Audiences */}
                  {customAudiences && Object.keys(customAudiences).length > 0 && 
                    getSortedAudiences(Object.entries(customAudiences)).map(([audience, subreddits]) => {
                      const metrics = audienceMetrics[audience] || { 
                        totalAudience: 0, 
                        growthPercentage: 0, 
                        isIncrease: true 
                      };
                      
                      return (
                        <Link 
                          to={`/audience/${audience}`} 
                          state={{ subreddits }} 
                          key={audience}
                          className="hover:opacity-90 transition-opacity"
                        >
                          <AudienceCard 
                            title={audience}
                            value={formatNumber(metrics.totalAudience)}
                            growth={`${metrics.isIncrease ? '+' : '-'}${Math.abs(metrics.growthPercentage)}%`}
                            period="vs last month"
                            subreddits={subreddits}
                            isGrowthPositive={metrics.isIncrease}
                            isLoading={loading && (!subreddits || subreddits.length === 0)}
                            isPro={isPro}
                          />
                        </Link>
                      );
                    })
                  }
                </div>
              )}
            </div>


          </div>
        </div>

        {/* Custom Audience Modal */}
        {showCustomAudienceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1c1c1c] rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Add Custom Audience</h3>
                <button
                  onClick={handleCloseCustomAudienceModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-4">
                <label htmlFor="audienceName" className="block text-sm font-medium text-gray-300 mb-2">
                  Audience Name
                </label>
                <input
                  type="text"
                  id="audienceName"
                  value={customAudienceName}
                  onChange={(e) => setCustomAudienceName(e.target.value)}
                  placeholder="e.g., Mobile App Developers, Travel Bloggers"
                  className="w-full px-3 py-2 bg-[#2a2a2a] border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={isCreatingAudience}
                />
                {customAudienceError && (
                  <p className="mt-2 text-sm text-red-400">{customAudienceError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseCustomAudienceModal}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                  disabled={isCreatingAudience}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCustomAudience}
                  disabled={isCreatingAudience || !customAudienceName.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCreatingAudience && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  {isCreatingAudience ? 'Creating...' : 'Create Audience'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showPricingModal && (
          <PricingModal 
            isOpen={showPricingModal}
            onClose={() => setShowPricingModal(false)}
            onSelectPlan={handleSelectPlan}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
